// Each person's dashboard setup, saved to their account so it follows them to any browser:
// home page, pinned and hidden pages, the sidebar, the clients picked, and the table layouts screens keep in storage.
// Screens keep reading and writing localStorage as before. On sign-in, startPreferenceSync()
// copies the account's values into localStorage before any screen opens; after that, changed keys
// go back to Supabase (dashboard_save_preferences) a moment later. Without Supabase the dashboard
// keeps working from browser storage alone.
import {supabase} from './supabase';

export const HOME_KEY='rnr-home',PINS_KEY='rnr-pins',HIDDEN_KEY='rnr-hidden';
/** Storage keys that belong to the person. Anything else in localStorage stays in the browser. */
const SYNCED=['rnr-home','rnr-pins','rnr-hidden','rnr-nav-collapsed','rnr-nav-closed','rnr-client-pick','llg-inquiry-columns-','llg-lead-database-layout-',
 'llg-lead-database-views-','llg-website-directory-columns-','llg-map-layout-','llg-main-kpi-','llg-tracker-','llg-workflow-drafts-','llg-email-review-notes-'];
const KEY=/^[A-Za-z0-9._:-]{1,120}$/;
const UUID=/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const MAX_VALUE=32_000;
const WATCH_MS=2_000;

export type Store=Pick<Storage,'getItem'|'setItem'|'removeItem'|'key'|'length'>;
export type SyncStatus={where:'account'|'browser';note?:string};
const browser=():Store|null=>{try{return window.localStorage;}catch{return null;}};

/** Synced keys only; a key naming another person's ID (shared computer) is never uploaded. */
export function isSyncedKey(key:string,userId:string):boolean{
 if(!KEY.test(key)||!SYNCED.some(p=>key===p||key.startsWith(p)))return false;
 const ids=key.match(UUID);
 return !ids||ids.every(id=>id.toLowerCase()===userId.toLowerCase());
}

export function snapshot(userId:string,store:Store|null=browser()):Record<string,string>{
 const out:Record<string,string>={};
 if(!store)return out;
 try{for(let i=0;i<store.length;i++){const k=store.key(i);if(!k||!isSyncedKey(k,userId))continue;const v=store.getItem(k);if(v!==null&&v.length<=MAX_VALUE)out[k]=v;}}catch{/* storage blocked */}
 return out;
}

/** The account wins for keys it has; keys only this browser has are returned for upload. */
export function hydrate(saved:Record<string,unknown>,userId:string,store:Store|null=browser()):Record<string,string>{
 const local=snapshot(userId,store),upload:Record<string,string>={};
 for(const [k,v] of Object.entries(saved))if(typeof v==='string'&&isSyncedKey(k,userId)&&local[k]!==v){try{store?.setItem(k,v);}catch{/* full or blocked */}}
 for(const [k,v] of Object.entries(local))if(!(k in saved))upload[k]=v;
 return upload;
}

/** Keys added or changed since `before`, and removed keys as null. */
export function changes(before:Record<string,string>,after:Record<string,string>):Record<string,string|null>{
 const out:Record<string,string|null>={};
 for(const [k,v] of Object.entries(after))if(before[k]!==v)out[k]=v;
 for(const k of Object.keys(before))if(!(k in after))out[k]=null;
 return out;
}

export function readList(key:string,store:Store|null=browser()):string[]{
 try{const v=JSON.parse(store?.getItem(key)||'[]');return Array.isArray(v)?[...new Set(v.filter((x):x is string=>typeof x==='string'))]:[];}catch{return [];}
}
export function writeList(key:string,list:string[],store:Store|null=browser()){
 try{if(list.length)store?.setItem(key,JSON.stringify(list));else store?.removeItem(key);}catch{/* per-browser fallback only */}
}
export function readText(key:string,store:Store|null=browser()):string|null{try{return store?.getItem(key)??null;}catch{return null;}}
export function writeText(key:string,value:string|null,store:Store|null=browser()){
 try{if(value)store?.setItem(key,value);else store?.removeItem(key);}catch{/* per-browser fallback only */}
}
/** Moves an item one place up (-1) or down (+1). */
export function shift<T>(list:T[],item:T,by:-1|1):T[]{
 const i=list.indexOf(item),j=i+by;
 if(i<0||j<0||j>=list.length)return list;
 const next=[...list];[next[i],next[j]]=[next[j],next[i]];return next;
}

let timer=0,user='',last:Record<string,string>={},pending:Record<string,string|null>={},sending=false,retryAt=0;
let status:SyncStatus={where:'browser',note:'Not signed in.'};
const listeners=new Set<(s:SyncStatus)=>void>();
const setStatus=(next:SyncStatus)=>{status=next;listeners.forEach(fn=>fn(next));};
export const syncStatus=()=>status;
export function onSyncStatus(fn:(s:SyncStatus)=>void){listeners.add(fn);return()=>{listeners.delete(fn);};}

async function send(){
 if(sending||!Object.keys(pending).length||Date.now()<retryAt)return;
 sending=true;const batch=pending;pending={};
 const {error}=await supabase.rpc('dashboard_save_preferences',{p_changes:batch});
 sending=false;
 if(error){
  pending={...batch,...pending};retryAt=Date.now()+60_000;
  setStatus({where:'browser',note:'Saved in this browser. Your account copy will catch up when the connection returns.'});
 }else{retryAt=0;if(status.where!=='account')setStatus({where:'account'});}
}
function watch(){
 const now=snapshot(user),diff=changes(last,now);
 last=now;
 if(Object.keys(diff).length)pending={...pending,...diff};
 void send();
}
const flushOnHide=()=>{if(document.visibilityState==='hidden')watch();};

/** Loads the account's setup into this browser, then keeps the account copy up to date. */
export async function startPreferenceSync(userId:string):Promise<SyncStatus>{
 stopPreferenceSync();
 user=userId;
 const {data,error}=await supabase.rpc('dashboard_preferences');
 if(error||!data||typeof data!=='object'||Array.isArray(data)){
  setStatus({where:'browser',note:'Your setup is saved in this browser only: your account copy could not be loaded.'});
  return status;
 }
 pending=hydrate(data as Record<string,unknown>,userId);
 setStatus({where:'account'});
 last=snapshot(userId);
 timer=window.setInterval(watch,WATCH_MS);
 document.addEventListener('visibilitychange',flushOnHide);
 void send();
 return status;
}
export function stopPreferenceSync(){
 if(timer)window.clearInterval(timer);
 timer=0;pending={};last={};retryAt=0;
 document.removeEventListener('visibilitychange',flushOnHide);
}
/** Saves any change made in the last moments right away (after an explicit action). */
export function savePreferencesNow(){if(user&&timer)watch();}
/** Forgets every synced key for this person, in this browser and in their account. */
export async function resetPreferences(userId:string,store:Store|null=browser()){
 const keys=Object.keys(snapshot(userId,store));
 keys.forEach(k=>{try{store?.removeItem(k);}catch{/* blocked */}});
 if(!timer)return;
 last=snapshot(userId,store);
 pending={...pending,...Object.fromEntries(keys.map(k=>[k,null]))};
 retryAt=0;
 await send();
}
